import { ActionIcon, Box, Group, Stack, Text, Title } from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

/**
 * One screen: its header, and the column of cards under it.
 *
 * Replaces `<main className="page">` plus `.page__header` as each screen is
 * migrated. Adopting this is what "restyled" means for a screen - which is
 * why it is a wrapper rather than something AppShell draws for everybody: a
 * screen still on the legacy markup would otherwise get two headers.
 *
 * The mobile header is sticky at 56px and the desktop one is a page title
 * block, per the brief. Both are this one component, because the alternative
 * is two headers whose wording drifts apart.
 */
export function Page({
  title,
  subtitle,
  /** Where the back arrow goes. Omit on a module landing reachable from the nav. */
  back,
  /** Header-right: an overflow menu, an avatar, a small action. */
  actions,
  /** The page's primary action, which on mobile belongs in a StickyActionBar. */
  children,
  /** Content width. "wide" for tables and ledgers that need the room. */
  width = "normal",
}: {
  title: string;
  subtitle?: ReactNode;
  back?: string;
  actions?: ReactNode;
  children: ReactNode;
  width?: "normal" | "wide";
}) {
  const navigate = useNavigate();

  return (
    <Box
      component="main"
      style={{
        background: "var(--mantine-color-gray-0)",
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        component="header"
        style={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          minHeight: 56,
          background: "var(--mantine-color-white)",
          borderBottom: "1px solid var(--mantine-color-gray-3)",
        }}
      >
        <Group
          px="md"
          py="sm"
          gap="sm"
          wrap="nowrap"
          justify="space-between"
          mx="auto"
          w="100%"
          maw={width === "wide" ? 1200 : 760}
        >
          <Group gap="sm" wrap="nowrap" style={{ minWidth: 0 }}>
            {back && (
              <ActionIcon
                variant="subtle"
                color="gray"
                aria-label="Back"
                onClick={() => navigate(back)}
              >
                <IconArrowLeft size={20} stroke={1.8} />
              </ActionIcon>
            )}
            <Stack gap={0} style={{ minWidth: 0 }}>
              <Title order={4} lh={1.2} style={{ overflowWrap: "anywhere" }}>
                {title}
              </Title>
              {subtitle && (
                <Text fz="sm" c="gray.6" lh={1.3}>
                  {subtitle}
                </Text>
              )}
            </Stack>
          </Group>
          {actions && (
            <Group gap="xs" wrap="nowrap">
              {actions}
            </Group>
          )}
        </Group>
      </Box>

      <Box
        px="md"
        py="md"
        mx="auto"
        w="100%"
        maw={width === "wide" ? 1200 : 760}
        style={{ flex: 1 }}
      >
        <Stack gap="md">{children}</Stack>
      </Box>
    </Box>
  );
}

/**
 * A page's standing explanation, as a footnote.
 *
 * WHY THIS IS NOT A SECTION CARD
 *
 * It was one, on every screen that had something to explain, and on most of
 * them it was the largest thing on the page - three paragraphs of standing
 * reference outweighing the four actions somebody actually came to take. The
 * words earn their place (they are what tells an operator that billing is the
 * only thing that moves stock, or that a handover needs all three proofs), so
 * they stay. They stop being the page.
 *
 * WHY IT LIVES HERE RATHER THAN IN EACH SCREEN
 *
 * The first version of this was a `<Box component="footer">` written inline
 * inside ModuleLanding, which meant only the screens that had adopted
 * ModuleLanding got it. Service and Delivery draw their own tile grids -
 * their tiles are role-conditional - so both kept the old card and the change
 * looked, from the outside, like it had not been made. One component, used by
 * all three, is the difference between a fix and a fix on two screens out of
 * four.
 */
export function PageNote({
  title,
  children,
}: {
  /** A short label - "Before you start". Omit where the prose speaks for itself. */
  title?: string;
  children: ReactNode;
}) {
  return (
    <Box
      component="footer"
      mt="xl"
      pt="md"
      style={{ borderTop: "1px solid var(--mantine-color-gray-2)" }}
    >
      <Stack gap={6}>
        {title && (
          <Text
            fz={11}
            fw={700}
            c="gray.6"
            tt="uppercase"
            style={{ letterSpacing: ".06em" }}
          >
            {title}
          </Text>
        )}
        {children}
      </Stack>
    </Box>
  );
}

/**
 * A paragraph inside a PageNote.
 *
 * 13px and grey, deliberately quieter than the body: reference, read once and
 * remembered, not something to work through every time the screen opens.
 */
export function NoteText({ children }: { children: ReactNode }) {
  return (
    <Text fz={13} c="gray.6" lh={1.5}>
      {children}
    </Text>
  );
}
